import { useState, useCallback, useMemo, useRef } from 'react';

/**
 * Manages sidebar node multi-selection state with support for:
 * - Single click: select one node (clears previous). Re-clicking deselects that node.
 * - Cmd/Ctrl+click: add nodes to selection; re-clicking increments count (adds duplicates on drag).
 * - Shift+click: range select between anchor and target
 */
export function useNodeSelection() {
  // Maps nodeId → count (how many copies to add when dragged)
  const [selectionCounts, setSelectionCounts] = useState<Map<string, number>>(
    new Map(),
  );
  // Anchor node for Shift+click range selection
  const lastSelectedNodeIdRef = useRef<string | null>(null);

  // Derived Set for backward-compatible boolean checks (has/size)
  const selectedNodeIds = useMemo(
    () => new Set(selectionCounts.keys()),
    [selectionCounts],
  );

  const selectSingle = useCallback((nodeId: string) => {
    setSelectionCounts((prev) => {
      if (prev.has(nodeId)) {
        const next = new Map(prev);
        next.delete(nodeId);
        lastSelectedNodeIdRef.current =
          next.size > 0 ? [...next.keys()][0] : null;
        return next;
      }
      lastSelectedNodeIdRef.current = nodeId;
      return new Map([[nodeId, 1]]);
    });
  }, []);

  const toggleSelect = useCallback((nodeId: string) => {
    lastSelectedNodeIdRef.current = nodeId;
    setSelectionCounts((prev) => {
      const next = new Map(prev);
      if (next.has(nodeId)) {
        // Already selected: increment count for duplicates
        next.set(nodeId, (prev.get(nodeId) ?? 1) + 1);
      } else {
        next.set(nodeId, 1);
      }
      return next;
    });
  }, []);

  /**
   * Selects all nodes between the anchor (lastSelectedNodeId) and the target,
   * based on the provided ordered list of visible node IDs.
   */
  const selectRange = useCallback(
    (targetNodeId: string, orderedNodeIds: string[]) => {
      const anchorId = lastSelectedNodeIdRef.current;
      if (!anchorId) {
        setSelectionCounts(new Map([[targetNodeId, 1]]));
        lastSelectedNodeIdRef.current = targetNodeId;
        return;
      }

      const anchorIndex = orderedNodeIds.indexOf(anchorId);
      const targetIndex = orderedNodeIds.indexOf(targetNodeId);

      if (anchorIndex === -1 || targetIndex === -1) {
        setSelectionCounts(new Map([[targetNodeId, 1]]));
        lastSelectedNodeIdRef.current = targetNodeId;
        return;
      }

      const start = Math.min(anchorIndex, targetIndex);
      const end = Math.max(anchorIndex, targetIndex);
      const rangeIds = orderedNodeIds.slice(start, end + 1);
      setSelectionCounts(new Map(rangeIds.map((id) => [id, 1])));
    },
    [],
  );

  /** Selects a specific set of nodes (clears previous selection). Empty array deselects all. */
  const selectMultiple = useCallback((nodeIds: string[]) => {
    lastSelectedNodeIdRef.current = nodeIds.length > 0 ? nodeIds[0] : null;
    setSelectionCounts(new Map(nodeIds.map((id) => [id, 1])));
  }, []);

  /** Adds nodes to the existing selection without clearing previous ones. */
  const addToSelection = useCallback((nodeIds: string[]) => {
    setSelectionCounts((prev) => {
      const next = new Map(prev);
      nodeIds.forEach((id) => {
        if (!next.has(id)) next.set(id, 1);
      });
      return next;
    });
    if (nodeIds.length > 0) {
      lastSelectedNodeIdRef.current = nodeIds[nodeIds.length - 1];
    }
  }, []);

  const isSelected = useCallback(
    (nodeId: string) => selectionCounts.has(nodeId),
    [selectionCounts],
  );

  /** Returns the repeat count for a selected node (0 if not selected). */
  const getCount = useCallback(
    (nodeId: string) => selectionCounts.get(nodeId) ?? 0,
    [selectionCounts],
  );

  const clearSelection = useCallback(() => {
    lastSelectedNodeIdRef.current = null;
    setSelectionCounts(new Map());
  }, []);

  const selectedCount = useMemo(() => selectionCounts.size, [selectionCounts]);

  /** Total count including duplicates (e.g. a node with count=3 contributes 3) */
  const totalSelectedCount = useMemo(() => {
    let total = 0;
    for (const count of selectionCounts.values()) {
      total += count;
    }
    return total;
  }, [selectionCounts]);

  return {
    selectedNodeIds,
    selectionCounts,
    selectSingle,
    selectMultiple,
    addToSelection,
    toggleSelect,
    selectRange,
    isSelected,
    getCount,
    clearSelection,
    selectedCount,
    totalSelectedCount,
  };
}
